import React from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Copy, Mail, MessageCircle, ExternalLink, Twitter } from "lucide-react";

/** Reusable share modal — mirrors the mobile app's TRANSMIT overlay.
 * Rendered via createPortal into document.body so it never nests inside
 * a parent <a>/<Link> card. */
export default function ShareModal({ open, onClose, url, title, testid = "share-modal" }) {
  if (!open || typeof document === "undefined") return null;
  const encodedText = encodeURIComponent(title + " — " + url);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copy = () => {
    const doneFallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("URL COPIED TO CLIPBOARD");
      } catch { toast.error("COPY UNAVAILABLE — SELECT MANUALLY"); }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => toast.success("URL COPIED TO CLIPBOARD"))
        .catch(doneFallback);
    } else { doneFallback(); }
    onClose();
  };

  const items = [
    { icon: MessageCircle, label: "WHATSAPP", href: `https://wa.me/?text=${encodedText}` },
    { icon: Twitter, label: "X · TWITTER", href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}` },
    { icon: Mail, label: "EMAIL", href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}` },
    { icon: Copy, label: "COPY LINK", onClick: copy },
    { icon: ExternalLink, label: "OPEN URL", href: url },
  ];

  const overlay = (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      data-testid={testid}>
      <div className="sat-surface sat-brackets max-w-md w-full p-6 relative"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
        <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
          data-testid={`${testid}-close`}
          className="absolute top-3 right-3 p-1.5 border border-[color:var(--sat-border)] text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)] hover:border-[color:var(--sat-cyan)]">
          <X size={14}/>
        </button>
        <div className="sat-label sat-cyan mb-1">TRANSMIT DOSSIER</div>
        <div className="font-display font-bold text-lg mb-5 line-clamp-2">{title}</div>
        <div className="grid grid-cols-2 gap-2">
          {items.map(({ icon: I, label, href, onClick }) => (
            href ? (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="sat-btn justify-center !p-3" data-testid={`${testid}-${label.split(' ')[0].toLowerCase()}`}>
                <I size={13} /> {label}
              </a>
            ) : (
              <button key={label} onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(); }}
                className="sat-btn justify-center !p-3" data-testid={`${testid}-copy`}>
                <I size={13} /> {label}
              </button>
            )
          ))}
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
